import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";

// English source catalog (t() returns these; the visual DOM translation engine then
// renders them in the chosen language so EVERYTHING in the app translates).
export const CATALOG = {
  home: "Home", live_consult: "Live Consult & AI Rx", book_appointment: "Book Appointment",
  ask_me: "Ask Me",
  booked_appointments: "Booked Appointments", prescription_pad: "Prescription Pad",
  patients_followups: "Patients & Follow-ups", patient_data: "Patient Data", medicines_labs: "Medicines & Labs", profile: "Profile",
  sign_out: "Sign out", ready_title: "Ready when you are, Doctor.",
  ready_sub: "Start a live consultation and let MDvia Care draft generic-only prescriptions you can edit in seconds.",
  start_live_consult: "Start Live Consult", total_patients: "Total Patients", consulted_today: "Consulted Today",
  followups_due_today: "Follow-ups Due Today", upcoming_followups: "Upcoming Follow-ups",
  gentle_reminders: "Gentle Follow-up Reminders", recent_consultations: "Recent Consultations",
  view_all: "View all", language: "Interface Language", country: "Country",
  watch_demo: "Watch demo", teleconsults: "Teleconsults",
};

const I18nContext = createContext(null);

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA", "CODE", "PRE"]);
const hasLetter = (s) => /[a-zA-ZÀ-ÿĀ-ॿ]/.test(s);

export function I18nProvider({ children }) {
  const [language, setLanguageState] = useState(localStorage.getItem("aura_lang") || "English");
  const [translating, setTranslating] = useState(false);
  const cacheRef = useRef({});
  const observerRef = useRef(null);
  const timerRef = useRef(null);

  const getCache = (lang) => {
    if (!cacheRef.current[lang]) {
      try { cacheRef.current[lang] = JSON.parse(localStorage.getItem(`aura_i18n2_${lang}`) || "{}"); }
      catch { cacheRef.current[lang] = {}; }
    }
    return cacheRef.current[lang];
  };

  const restoreEnglish = useCallback(() => {
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    let n;
    while ((n = walker.nextNode())) {
      if (n.__i18nOrig !== undefined && n.textContent !== n.__i18nOrig) n.textContent = n.__i18nOrig;
    }
    document.querySelectorAll("input[placeholder], textarea[placeholder]").forEach((el) => {
      if (el.__i18nPhOrig !== undefined) el.placeholder = el.__i18nPhOrig;
    });
  }, []);

  const collectStrings = useCallback(() => {
    const found = new Set();
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      const p = node.parentElement;
      if (!p || SKIP_TAGS.has(p.tagName)) continue;
      if (p.closest("[data-notranslate]")) continue;
      const orig = node.__i18nOrig !== undefined ? node.__i18nOrig : node.textContent;
      if (orig && orig.trim() && hasLetter(orig)) found.add(orig.trim());
    }
    document.querySelectorAll("input[placeholder], textarea[placeholder]").forEach((el) => {
      if (el.closest("[data-notranslate]")) return;
      const orig = el.__i18nPhOrig !== undefined ? el.__i18nPhOrig : el.placeholder;
      if (orig && orig.trim() && hasLetter(orig)) found.add(orig.trim());
    });
    return Array.from(found);
  }, []);

  const applyTranslations = useCallback((lang) => {
    const cache = getCache(lang);
    // text nodes
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      const p = node.parentElement;
      if (!p || SKIP_TAGS.has(p.tagName)) continue;
      if (p.closest("[data-notranslate]")) continue;
      const orig = (node.__i18nOrig !== undefined ? node.__i18nOrig : node.textContent).trim();
      if (!orig || !hasLetter(orig)) continue;
      const trans = cache[orig];
      if (trans && node.textContent !== trans) {
        node.__i18nOrig = orig;
        node.__i18nApplying = true;
        node.textContent = trans;
        node.__i18nApplying = false;
      } else if (trans) {
        node.__i18nOrig = orig;
      }
    }
    // placeholders
    document.querySelectorAll("input[placeholder], textarea[placeholder]").forEach((el) => {
      if (el.closest("[data-notranslate]")) return;
      const orig = (el.__i18nPhOrig !== undefined ? el.__i18nPhOrig : el.placeholder).trim();
      if (!orig || !hasLetter(orig)) return;
      const trans = cache[orig];
      if (trans) { el.__i18nPhOrig = orig; el.placeholder = trans; }
    });
  }, []);

  const translateNow = useCallback(async (lang) => {
    if (!lang || lang === "English") { restoreEnglish(); return; }
    const cache = getCache(lang);
    const strings = collectStrings().filter((s) => !cache[s]);
    if (strings.length) {
      setTranslating(true);
      try {
        for (let i = 0; i < strings.length; i += 120) {
          const chunk = strings.slice(i, i + 120);
          const r = await api.post("/i18n/translate-batch", { language: lang, strings: chunk });
          Object.assign(cache, r.data.translations || {});
        }
        localStorage.setItem(`aura_i18n2_${lang}`, JSON.stringify(cache));
      } catch {} finally { setTranslating(false); }
    }
    applyTranslations(lang);
  }, [collectStrings, applyTranslations, restoreEnglish]);

  const setLanguage = useCallback(async (lang) => {
    setLanguageState(lang);
    localStorage.setItem("aura_lang", lang);
    await translateNow(lang);
  }, [translateNow]);

  // Observe DOM for dynamic content and re-translate
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();
    if (language === "English") return;
    observerRef.current = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type === "characterData" && m.target.__i18nApplying) return;
      }
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => translateNow(language), 500);
    });
    observerRef.current.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => { observerRef.current && observerRef.current.disconnect(); clearTimeout(timerRef.current); };
  }, [language, translateNow]);

  // initial translate if a non-English language is active
  useEffect(() => {
    if (language !== "English") {
      const t = setTimeout(() => translateNow(language), 800);
      return () => clearTimeout(t);
    }
  }, []); // eslint-disable-line

  const t = useCallback((key) => CATALOG[key] || key, []);

  return (
    <I18nContext.Provider value={{ language, setLanguage, t, translating }}>
      {children}
      {translating && language !== "English" && (
        <div data-testid="translating-indicator" className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-2 rounded-full bg-[#123C73] text-white text-xs font-medium px-4 py-2 shadow-lg">
          <span className="w-3 h-3 rounded-full border-2 border-white/40 border-t-white animate-spin" />
          Translating to {language}…
        </div>
      )}
    </I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext);
